import { useQuery } from '@tanstack/react-query';
import type { NotificationItem, NotificationKind } from '@shared/types';
import type { IconName } from '@/ui/icons';
import { actions } from './actions';
import { api } from './api';
import { openRecord } from './overlays';
import { queryClient } from './queryClient';
import { toast, toastError } from './toast';

// Your in-app notifications: replies, opens, mentions, assignments, comments, delayed sends and finished AI searches.
// The bell, the notifications panel and the Home tile all read from here.

export interface NotificationList {
  items: NotificationItem[];
  unread: number;
}

const KEY = ['notifications'];

export function useNotifications(): { items: NotificationItem[]; unread: number; isLoading: boolean; isError: boolean; refetch: () => void } {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<NotificationList>('/notifications'),
    staleTime: 60_000,
  });
  return { items: data?.items ?? [], unread: data?.unread ?? 0, isLoading, isError, refetch: () => void refetch() };
}

/** Icon and tint for each kind, matching the design's notification rows. */
export const NOTIFICATION_LOOK: Record<NotificationKind, { icon: IconName; tint: string }> = {
  reply: { icon: 'reply', tint: 'reply' },
  open: { icon: 'eye', tint: 'open' },
  mention: { icon: 'at', tint: 'mention' },
  ai_done: { icon: 'sparkle', tint: 'ai' },
  assigned: { icon: 'reassign', tint: 'assign' },
  comment: { icon: 'comment', tint: 'comment' },
  send_delayed: { icon: 'clock', tint: 'delay' },
};

/** Updates the cached list straight away, so the badge and rows change before the server answers. */
function setReadLocally(ids: Set<string> | 'all', read: boolean): void {
  queryClient.setQueryData<NotificationList>(KEY, (old) => {
    if (!old) return old;
    if (ids === 'all' && read) return { items: old.items.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })), unread: 0 };
    let delta = 0;
    const items = old.items.map((n) => {
      if (ids !== 'all' && !ids.has(n.id)) return n;
      if (read && !n.readAt) {
        delta -= 1;
        return { ...n, readAt: new Date().toISOString() };
      }
      if (!read && n.readAt) {
        delta += 1;
        return { ...n, readAt: null };
      }
      return n;
    });
    return { items, unread: Math.max(0, old.unread + delta) };
  });
}

export async function markNotificationRead(id: string): Promise<void> {
  setReadLocally(new Set([id]), true);
  try {
    await api.post('/notifications/read', { ids: [id] });
  } catch (err) {
    void queryClient.invalidateQueries({ queryKey: KEY });
    toastError(err);
  }
}

/** Marks everything read, with Undo putting back exactly the ones that changed. */
export async function markAllNotificationsRead(): Promise<void> {
  setReadLocally('all', true);
  try {
    const { ids } = await api.post<{ ids: string[] }>('/notifications/read', { all: true });
    toast('All notifications marked as read', {
      icon: 'check-circle',
      undo: ids.length
        ? () => {
            setReadLocally(new Set(ids), false);
            api.post('/notifications/unread', { ids }).catch((err: unknown) => {
              void queryClient.invalidateQueries({ queryKey: KEY });
              toastError(err);
            });
          }
        : undefined,
    });
  } catch (err) {
    void queryClient.invalidateQueries({ queryKey: KEY });
    toastError(err);
  }
}

/** Takes you to what the notification is about. */
export function openNotificationTarget(n: NotificationItem): void {
  if (!n.entityId) return;
  switch (n.entityType) {
    case 'person':
    case 'company':
      openRecord(n.entityType, n.entityId, n.kind === 'comment' || n.kind === 'mention' ? { tab: 'comments' } : {});
      break;
    case 'deal':
      actions.openDeal(n.entityId);
      break;
    case 'thread':
      actions.openThread(n.entityId);
      break;
    case 'finder':
      actions.openFinder(n.entityId);
      break;
    case 'outbox':
      actions.openOutbox(n.entityId);
      break;
    default:
      break;
  }
}
